import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box, TextField, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { MAIN_URL } from "../../../Configurations/Urls";

const ADD_BUTTON_COLOR = "#0891B2";

const CHARTS = [
  { key: "by_program", title: "Training program" },
  { key: "by_country", title: "Country" },
  { key: "by_city", title: "City" },
];

function TrafficTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        px: 1.5,
        py: 1,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {row.label || "-"}
      </Typography>
      <Typography variant="caption" display="block">
        Monthly traffic: {row.monthly_traffic ?? 0}
      </Typography>
      <Typography variant="caption" display="block">
        Leads: {row.leads ?? 0}
      </Typography>
      <Typography variant="caption" display="block">
        Admissions: {row.admissions ?? 0}
      </Typography>
      <Typography variant="caption" display="block">
        Conversion: {row.conversion_percent ?? 0}%
      </Typography>
    </Box>
  );
}

function labelBandHeight(rows) {
  const longest = (Array.isArray(rows) ? rows : []).reduce(
    (max, row) => Math.max(max, String(row?.label || "").trim().length),
    0
  );
  return Math.max(36, Math.ceil(longest * 6.6 + 20));
}

function VerticalCategoryTick({ x = 0, y = 0, payload }) {
  const label =
    payload?.value == null || String(payload.value).trim() === ""
      ? "-"
      : String(payload.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <title>{label}</title>
      <text
        transform="rotate(-90)"
        x={-8}
        y={0}
        dy={4}
        textAnchor="end"
        fill="#64748b"
        fontSize={11}
      >
        {label}
      </text>
    </g>
  );
}

function TrafficChart({ title, rows }) {
  const data = Array.isArray(rows) ? rows : [];
  const labelSpace = labelBandHeight(data);
  const chartHeight = 208 + labelSpace;
  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 2,
        overflow: "visible",
      }}
    >
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      {data.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
          No traffic for this view.
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              interval={0}
              tick={<VerticalCategoryTick />}
              height={labelSpace}
              tickLine={false}
            />
            <YAxis allowDecimals={false} width={40} tick={{ fontSize: 11 }} />
            <Tooltip content={<TrafficTooltip />} />
            <Bar dataKey="monthly_traffic" fill={ADD_BUTTON_COLOR} radius={[4, 4, 0, 0]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
}

export default function ProgramMonthlyTrafficCharts({ organizationId }) {
  const [month, setMonth] = useState("");
  const [charts, setCharts] = useState({
    by_program: [],
    by_country: [],
    by_city: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!organizationId) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${MAIN_URL}/api/ems/program-monthly-traffic/charts`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          params: {
            organization_id: organizationId,
            ...(month ? { traffic_month: month } : {}),
          },
        });
        if (cancelled) return;
        const data = res.data?.data || {};
        setCharts({
          by_program: data.by_program || [],
          by_country: data.by_country || [],
          by_city: data.by_city || [],
        });
      } catch (error) {
        if (!cancelled) {
          setCharts({ by_program: [], by_country: [], by_city: [] });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId, month]);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5, gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Traffic charts
        </Typography>
        <TextField
          type="month"
          size="small"
          label="Month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            width: 180,
            bgcolor: "#ffffff",
            borderRadius: 1,
            "& .MuiOutlinedInput-root": {
              bgcolor: "#ffffff",
              "& fieldset": { borderColor: ADD_BUTTON_COLOR },
              "&:hover fieldset": { borderColor: ADD_BUTTON_COLOR },
              "&.Mui-focused fieldset": { borderColor: ADD_BUTTON_COLOR },
            },
            "& .MuiInputLabel-root": { color: ADD_BUTTON_COLOR },
            "& .MuiInputLabel-root.Mui-focused": { color: ADD_BUTTON_COLOR },
            "& .MuiInputBase-input": { color: ADD_BUTTON_COLOR },
            "& input::-webkit-calendar-picker-indicator": {
              filter:
                "invert(42%) sepia(86%) saturate(748%) hue-rotate(153deg) brightness(92%) contrast(96%)",
            },
          }}
        />
      </Box>
      {loading ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Loading charts...
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" },
          gap: 2,
        }}
      >
        {CHARTS.map((chart) => (
          <TrafficChart key={chart.key} title={chart.title} rows={charts[chart.key]} />
        ))}
      </Box>
    </Box>
  );
}
